
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Bell, Mail, Smartphone, BellOff } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { 
  getUserNotificationPreferences,
  updateUserNotificationPreferences,
  type NotificationPreference
} from '@/utils/notificationService';

interface NotificationSettingsProps {
  userId: string;
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({ userId }) => {
  const [preferences, setPreferences] = useState<NotificationPreference[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  
  useEffect(() => {
    const fetchNotificationPreferences = async () => {
      if (!userId) return;
      
      try {
        setIsLoading(true);
        const userPreferences = await getUserNotificationPreferences(userId);
        setPreferences(userPreferences);
      } catch (error) {
        console.error('Error fetching notification preferences:', error);
        toast({
          title: "Error",
          description: "Failed to load notification preferences",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchNotificationPreferences();
  }, [userId, toast]);
  
  const handleTogglePreference = (prefId: string) => {
    setPreferences(prev => 
      prev.map(pref => 
        pref.id === prefId ? { ...pref, enabled: !pref.enabled } : pref
      )
    );
  };
  
  const handleSavePreferences = async () => {
    try {
      setIsSaving(true);
      await updateUserNotificationPreferences(userId, preferences);
      toast({
        title: "Success",
        description: "Notification preferences updated",
      });
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      toast({
        title: "Error",
        description: "Failed to save notification preferences",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Bell className="mr-2 h-5 w-5 text-primary" />
          Notification Preferences
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {preferences.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <BellOff className="h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-500">No notification preferences found</p>
            </div>
          ) : (
            <>
              {preferences.map((pref) => (
                <div key={pref.id} className="flex items-center justify-between border-b pb-4">
                  <div className="flex items-center">
                    {pref.channel === 'email' ? (
                      <Mail className="h-5 w-5 mr-3 text-blue-500" />
                    ) : (
                      <Smartphone className="h-5 w-5 mr-3 text-green-500" />
                    )}
                    <div>
                      <Label htmlFor={pref.id} className="font-medium">
                        {pref.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} Notifications
                      </Label>
                      <p className="text-sm text-gray-500">
                        Via {pref.channel === 'email' ? 'email' : 'SMS'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    id={pref.id}
                    checked={pref.enabled}
                    onCheckedChange={() => handleTogglePreference(pref.id)}
                  />
                </div>
              ))}
              
              <div className="flex justify-end pt-4">
                <Button 
                  onClick={handleSavePreferences}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Preferences'}
                </Button>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default NotificationSettings;
